import {
  Avatar, Paper, Box, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as CancelIcon } from '@mui/icons-material';
import JSZip from 'jszip';

// Shared visual building blocks for the Supervisor-facing report/approval
// pages (SupervisorReports.js, WorkerVisitDetail.js, Approvals.js) —
// extracted so all of them use the exact same look instead of duplicating it.

export const FORM_TYPE_TABS = [
  { key: 'all', label: 'ALL' },
  { key: 'hbnc', label: 'HBNC' },
  { key: 'hbyc', label: 'HBYC' },
  { key: 'nrc', label: 'NRC' },
  { key: 'sncu', label: 'SNCU' },
];

// Same per-form-type palette as the mobile app's DraftsScreen.js, so a form
// type reads as the same colour everywhere a worker or supervisor sees it.
export const FORM_TYPE_STYLES = {
  hbnc: { color: '#2E7D32', bgColor: '#E8F5E9' },
  hbyc: { color: '#1976D2', bgColor: '#E3F2FD' },
  sncu: { color: '#00838F', bgColor: '#E0F7FA' },
  nrc: { color: '#7B1FA2', bgColor: '#F3E5F5' },
};

// Visit-day labels, scoped per form type — "visit_3" means day 3 for HBNC
// but month 3 for HBYC/SNCU, so this must never be one flat dict.
// Each visit's tenure, shown in its column header as "भेट १ (दिवस ३)" etc.
export const VISIT_LABELS = {
  hbnc: { visit_1: 'दिवस १', visit_3: 'दिवस ३', visit_7: 'दिवस ७', visit_14: 'दिवस १४', visit_21: 'दिवस २१', visit_28: 'दिवस २८', visit_42: 'दिवस ४२' },
  hbyc: { visit_3: '३ महिने', visit_6: '६ महिने', visit_9: '९ महिने', visit_12: '१२ महिने', visit_15: '१५ महिने' },
  nrc: { visit_15days: '१५ दिवस', visit_1month: '१ महिना', visit_2months: '२ महिने', visit_3months: '३ महिने', visit_6months: '६ महिने' },
  sncu: { visit_3: '३ महिने', visit_6: '६ महिने', visit_9: '९ महिने', visit_12: '१२ महिने' },
};

const MARATHI_DIGITS = ['१', '२', '३', '४', '५', '६', '७', '८', '९', '१०'];

// The backend sends the visit's own date as DD/MM/YYYY — displayed here as
// "dd / mm / yy" (spaced slashes, 2-digit year) to stay compact in narrow
// per-visit-day table cells.
export const formatVisitDate = (dmy) => {
  const [d, m, y] = dmy.split('/');
  return `${d} / ${m} / ${(y || '').slice(-2)}`;
};

export function VisitBadge({ value }) {
  // A tiny visual cue so "0 भेटी" doesn't read the same as "17 भेटी" — no
  // hardcoded thresholds beyond "zero is muted, everything else is live".
  const color = value === 0 ? 'default' : 'success';
  return <Chip label={value} size="small" color={color} variant={value === 0 ? 'outlined' : 'filled'} sx={{ fontWeight: 700, minWidth: 42 }} />;
}

export function MotherVisitTable({ mothers, visitDays, formType, maxHeight, isMonthFiltered }) {
  const labels = VISIT_LABELS[formType] || {};
  // "एकूण भेटी" (total visits) only means "all time" with no month filter —
  // with one applied, the count is scoped to that month, so the column
  // header must say so instead of implying a running lifetime total.
  const totalLabel = isMonthFiltered ? 'या महिन्यातील भेटी' : 'एकूण भेटी';
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5, borderColor: 'grey.200', ...(maxHeight ? { maxHeight } : {}) }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 700, color: 'grey.700', fontSize: '0.836rem' } }}>
            <TableCell>माता</TableCell>
            <TableCell>ABHA क्रमांक</TableCell>
            {visitDays.map((day, index) => (
              <TableCell key={day} align="center">
                भेट {MARATHI_DIGITS[index] || index + 1} ({labels[day] || day})
              </TableCell>
            ))}
            <TableCell align="right">{totalLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mothers.map((m) => (
            <TableRow key={m.formId} hover>
              <TableCell sx={{ fontWeight: 600 }}>{m.motherName}</TableCell>
              <TableCell sx={{ color: 'grey.600', fontVariantNumeric: 'tabular-nums', fontSize: '0.88rem' }}>{m.motherAbha || '-'}</TableCell>
              {visitDays.map((day) => (
                <TableCell key={day} align="center">
                  {m.visits[day] ? (
                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <CheckIcon color="success" fontSize="small" />
                      <Typography sx={{ fontSize: '0.682rem', color: 'grey.500', fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                        {formatVisitDate(m.visits[day])}
                      </Typography>
                    </Box>
                  ) : (
                    <CancelIcon sx={{ color: 'grey.300' }} fontSize="small" />
                  )}
                </TableCell>
              ))}
              <TableCell align="right"><VisitBadge value={m.totalVisits} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    transition: 'box-shadow 0.15s ease',
    '&:hover': { boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.06)' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.12)' },
  },
};

export function downloadBlob(data, filename) {
  const blob = new Blob([data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// The server's worker export has no period line and writes "एकूण भेटी" even
// when a month is filtered, so fix both here: insert a "कालावधी" row right after
// the "उप-केंद्र" row, and relabel the total column when a month is applied.
// CSV is edited as text; XLSX rewrites each worksheet part of the zip.
export async function relabelExport(blob, format, { relabelTotal, tenureLabel, formType }) {
  const from = 'एकूण भेटी';
  const to = 'या महिन्यातील भेटी';
  const tenureKey = 'कालावधी';
  const subCentreKey = 'उप-केंद्र';

  // Visit headers become "भेट १ (दिवस १)" — the same text as the web table —
  // matched by position within each form section's header row.
  const visitHeaderFor = (type, index) => {
    const labels = VISIT_LABELS[type];
    const keys = labels ? Object.keys(labels) : [];
    if (index >= keys.length) return null;
    return `भेट ${MARATHI_DIGITS[index] || index + 1} (${labels[keys[index]]})`;
  };
  const sectionType = (text) => {
    const match = /^— ([A-Z]+) —$/.exec(text || '');
    return match ? match[1].toLowerCase() : null;
  };

  if (format === 'csv') {
    let text = await blob.text();
    if (relabelTotal) text = text.split(from).join(to);
    const lines = text.split('\n');
    let currentType = formType !== 'all' ? formType : null;
    const relabelled = lines.map((line) => {
      const cells = line.split(',');
      const marker = sectionType(cells[0]);
      if (marker) currentType = marker;
      if (!currentType || cells[0] !== 'माता') return line;
      let visitIndex = 0;
      return cells.map((cell) => {
        if (!cell.startsWith('भेट ')) return cell;
        const next = visitHeaderFor(currentType, visitIndex++);
        return next ?? cell;
      }).join(',');
    });
    const idx = relabelled.findIndex((line) => line.startsWith(subCentreKey));
    if (idx !== -1 && tenureLabel) relabelled.splice(idx + 1, 0, `${tenureKey},${tenureLabel}`);
    return new Blob([relabelled.join('\n')], { type: 'text/csv;charset=utf-8' });
  }

  const escapeXml = (value) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const insertTenureRow = (xml) => {
    const match = xml.match(/<row r="(\d+)"[^>]*>(?:(?!<\/row>).)*<t>उप-केंद्र<\/t>(?:(?!<\/row>).)*<\/row>/s);
    if (!match || !tenureLabel) return xml;
    const afterRow = Number(match[1]) + 1;
    const shifted = xml.replace(/<row r="(\d+)"/g, (m, r) => (Number(r) >= afterRow ? `<row r="${Number(r) + 1}"` : m))
      .replace(/<c r="([A-Z]+)(\d+)"/g, (m, col, r) => (Number(r) >= afterRow ? `<c r="${col}${Number(r) + 1}"` : m));
    const newRow = `<row r="${afterRow}"><c r="A${afterRow}" t="inlineStr"><is><t>${tenureKey}</t></is></c>`
      + `<c r="B${afterRow}" t="inlineStr"><is><t>${escapeXml(tenureLabel)}</t></is></c></row>`;
    const insertAt = shifted.indexOf('</row>', shifted.indexOf(`<row r="${match[1]}"`)) + '</row>'.length;
    return shifted.slice(0, insertAt) + newRow + shifted.slice(insertAt);
  };

  const relabelVisitHeaders = (xml) => {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    let currentType = formType !== 'all' ? formType : null;
    for (const row of Array.from(doc.getElementsByTagName('row'))) {
      const cells = Array.from(row.getElementsByTagName('c'));
      const texts = cells.map((c) => {
        const t = c.getElementsByTagName('t')[0];
        return t ? t.textContent : '';
      });
      const marker = sectionType(texts[0]);
      if (marker) currentType = marker;
      if (!currentType || texts[0] !== 'माता') continue;
      const rowType = currentType;
      let visitIndex = 0;
      cells.forEach((c, i) => {
        if (!texts[i].startsWith('भेट ')) return;
        const next = visitHeaderFor(rowType, visitIndex++);
        const t = c.getElementsByTagName('t')[0];
        if (next && t) t.textContent = next;
      });
    }
    return new XMLSerializer().serializeToString(doc);
  };

  const zip = await JSZip.loadAsync(blob);
  const parts = Object.keys(zip.files).filter(
    (name) => /^xl\/worksheets\/[^/]+\.xml$/.test(name) || name === 'xl/sharedStrings.xml'
  );
  for (const part of parts) {
    let xml = await zip.file(part).async('string');
    if (relabelTotal) xml = xml.split(from).join(to);
    if (part.startsWith('xl/worksheets/')) {
      xml = relabelVisitHeaders(xml);
      xml = insertTenureRow(xml);
    }
    zip.file(part, xml);
  }
  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function nameInitial(name) {
  return (name || '?').trim().charAt(0);
}

export function SummaryCard({ title, value, icon: Icon, color }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        borderColor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flex: 1,
        // flex:1 alone gives flex-basis:0, so these would always just keep
        // shrinking to fit one row instead of ever triggering the parent's
        // flexWrap — minWidth:0 made that worse by removing the one thing
        // (unshrinkable content) that would've forced a wrap. A real floor
        // here means 3 cards genuinely don't fit one line on a phone, so
        // they stack instead of squeezing to unreadable width or overflowing.
        minWidth: { xs: '100%', sm: 180 },
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.07)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Avatar sx={{ bgcolor: `${color}.lighter`, color: `${color}.main`, width: 50, height: 50 }}>
        <Icon />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '1.87rem', fontWeight: 800, color: 'grey.900', lineHeight: 1.15 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: '0.894rem', color: 'grey.600', whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
      </Box>
    </Paper>
  );
}
